import React, {useEffect, useRef, useState} from "react";
import {AlertCircle, Eye, EyeOff, Loader2} from "lucide-react";
import {useDispatch} from "react-redux";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {Button} from "../../components/nuclear/button";
import {Checkbox} from "../../components/nuclear/checkbox";
import {Input} from "../../components/nuclear/input";
import {Label} from "../../components/nuclear/label";
import "../../components/nuclear/ui.css";
import "./SignUpPage.module.css";
import "./GoogleOAuthCallbackPage.module.css";
import {PrivacyModal} from "../../pages/authentication/PrivacyModal";
import {TermsModal} from "../../pages/authentication/TermsModal";
import {
    confirmUserSignUp,
    completeAutoSignIn,
    signUpUser,
    validatePreferredUsername,
} from "./authServices";
// Google sign-up disabled — see docs/deployment/google-integration-shelved.md
// import {getGoogleAuthFailureMessage, signInWithGoogle} from "./authServices";
import {completeAuthentication, useAuthenticationPage} from "./authPageHelpers";
import { getPasswordValidationError, isValidEmail } from "./authValidation";

export default function SignUpPage({redirection}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [preferredUsername, setPreferredUsername] = useState("");
    const [email, setEmail] = useState("");
    const [confirmationCode, setConfirmationCode] = useState("");
    const [mailingListOptIn, setMailingListOptIn] = useState("no");
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [pendingConfirmationEmail, setPendingConfirmationEmail] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const displayNameInputRef = useRef(null);
    const emailInputRef = useRef(null);
    const confirmationCodeInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const confirmPasswordInputRef = useRef(null);

    useAuthenticationPage(dispatch, redirection);

    useEffect(() => {
        document.title = pendingConfirmationEmail
            ? "Confirm Account - Animotive VR Dashboard"
            : "Create Account - Animotive VR Dashboard";
        return () => {
            document.title = "Animotive VR Dashboard";
        };
    }, [pendingConfirmationEmail]);

    useEffect(() => {
        if (pendingConfirmationEmail) {
            confirmationCodeInputRef.current?.focus();
        }
    }, [pendingConfirmationEmail]);

    function buildRegistrationPayload() {
        const trimmedDisplayName = preferredUsername.trim();
        const trimmedEmail = email.trim();
        const password = passwordInputRef.current?.value ?? "";
        const confirmPassword = confirmPasswordInputRef.current?.value ?? "";

        if (!trimmedDisplayName) {
            setErrorMessage("Display name is required");
            displayNameInputRef.current?.focus();
            return null;
        }

        if (trimmedDisplayName.length < 2) {
            setErrorMessage("Display name must be at least 2 characters long");
            displayNameInputRef.current?.focus();
            return null;
        }

        const displayNameError = validatePreferredUsername(trimmedDisplayName);
        if (displayNameError) {
            setErrorMessage(displayNameError);
            displayNameInputRef.current?.focus();
            return null;
        }

        if (!trimmedEmail) {
            setErrorMessage("Email is required");
            emailInputRef.current?.focus();
            return null;
        }

        if (!isValidEmail(trimmedEmail)) {
            setErrorMessage("Please enter a valid email address (e.g., user@example.com)");
            emailInputRef.current?.focus();
            return null;
        }

        const passwordError = getPasswordValidationError(password);
        if (passwordError) {
            setErrorMessage(passwordError);
            passwordInputRef.current?.focus();
            return null;
        }

        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match");
            confirmPasswordInputRef.current?.focus();
            return null;
        }

        if (!acceptedTerms || !acceptedPrivacyPolicy) {
            setErrorMessage("Please accept the terms and privacy policy to continue");
            return null;
        }

        return {
            email: trimmedEmail,
            password,
            preferredUsername: trimmedDisplayName,
            mailingListOptIn: mailingListOptIn === "yes" ? "true" : "false",
        };
    }

    async function completeSignedUpUser(signUpStep) {
        if (signUpStep === "COMPLETE_AUTO_SIGN_IN") {
            await completeAutoSignIn();
        }

        await completeAuthentication(dispatch, navigate, {redirectToSurvey: true});
    }

    async function handleDetailsSubmit(event) {
        event.preventDefault();
        setErrorMessage(null);

        const payload = buildRegistrationPayload();
        if (!payload) {
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await signUpUser(payload);
            const signUpStep = response?.nextStep?.signUpStep;

            if (signUpStep === "CONFIRM_SIGN_UP") {
                setPendingConfirmationEmail(payload.email);
                setConfirmationCode("");
                return;
            }

            await completeSignedUpUser(signUpStep);
        } catch (error) {
            console.error("Registration failed:", error);
            setErrorMessage(error?.message ?? "Registration failed. Please try again or use a different email.");
            emailInputRef.current?.focus();
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleConfirmSubmit(event) {
        event.preventDefault();
        setErrorMessage(null);

        const trimmedCode = confirmationCode.trim();
        if (!trimmedCode) {
            setErrorMessage("Verification code is required");
            confirmationCodeInputRef.current?.focus();
            return;
        }

        setIsSubmitting(true);
        try {
            const confirmationResponse = await confirmUserSignUp({
                username: pendingConfirmationEmail,
                code: trimmedCode,
            });

            await completeSignedUpUser(confirmationResponse?.nextStep?.signUpStep);
        } catch (error) {
            console.error("Registration failed:", error);
            setErrorMessage(error?.message ?? "Unable to confirm your account. Please try again.");
            confirmationCodeInputRef.current?.focus();
        } finally {
            setIsSubmitting(false);
        }
    }

    // Google sign-up disabled — see docs/deployment/google-integration-shelved.md
    // async function handleGoogleSignUp() {
    //     setErrorMessage(null);
    //
    //     try {
    //         await signInWithGoogle("signup");
    //     } catch (error) {
    //         setErrorMessage(getGoogleAuthFailureMessage(error));
    //     }
    // }

    const isAwaitingConfirmation = Boolean(pendingConfirmationEmail);

    return (
        <div className={"auth-page-shell"}>
            <div className={"auth-page-container"}>
                <img
                    src={"/img/Branding/AnimotiveLogoHeader.png"}
                    alt={"Animotive"}
                    className={"auth-logo"}
                />

                <div className={"auth-tabs"} role={"tablist"}>
                    <RouterLink
                        to={"/"}
                        role={"tab"}
                        aria-selected={"false"}
                        className={"auth-tab-link"}
                    >
                        Log In
                    </RouterLink>
                    <button
                        type={"button"}
                        role={"tab"}
                        aria-selected={"true"}
                        className={"auth-tab-button"}
                    >
                        Create Account
                    </button>
                </div>

                {errorMessage && (
                    <div
                        id={"register-error"}
                        role={"alert"}
                        aria-live={"assertive"}
                        aria-atomic={"true"}
                        className={"auth-error-alert"}
                    >
                        <AlertCircle size={18} className={"auth-alert-icon"} />
                        <span>{errorMessage}</span>
                    </div>
                )}

                {isSubmitting && (
                    <div role={"status"} aria-live={"polite"} className={"sr-only"}>
                        {isAwaitingConfirmation
                            ? "Confirming your account, please wait..."
                            : "Creating your account, please wait..."}
                    </div>
                )}

                {isAwaitingConfirmation ? (
                    <form onSubmit={handleConfirmSubmit} className={"auth-form-grid"} noValidate={true}>
                        <h1 className={"auth-signup-confirm-heading"}>Confirm your account</h1>
                        <p className={"auth-signup-confirm-copy"}>
                            Enter the verification code we sent to {pendingConfirmationEmail} to finish creating your account.
                        </p>
                        <div>
                            <Label htmlFor={"sign-up-code"} className={"auth-field-label"}>
                                Code
                                <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                            </Label>
                            <Input
                                ref={confirmationCodeInputRef}
                                id={"sign-up-code"}
                                type={"text"}
                                name={"confirmationCode"}
                                autoComplete={"one-time-code"}
                                value={confirmationCode}
                                onChange={(event) => {
                                    setConfirmationCode(event.target.value);
                                    setErrorMessage(null);
                                }}
                                placeholder={"Enter verification code"}
                                required={true}
                                aria-required={"true"}
                                aria-invalid={errorMessage && !confirmationCode.trim() ? "true" : "false"}
                                aria-describedby={errorMessage ? "register-error" : "sign-up-code-note"}
                            />
                        </div>

                        <Button
                            type={"submit"}
                            disabled={isSubmitting}
                            aria-busy={isSubmitting ? "true" : "false"}
                            fullWidth={true}
                        >
                            {isSubmitting ? <Loader2 size={18}/> : null}
                            {isSubmitting ? "Confirming..." : "Confirm"}
                        </Button>

                        <p id={"sign-up-code-note"} className={"auth-signup-confirm-note"}>
                            Check your inbox for the verification code, then confirm your account to continue.
                        </p>

                        <div className={"auth-link-row auth-link-row--center"}>
                            <RouterLink to={"/"} className={"auth-link"}>
                                Back to login
                            </RouterLink>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleDetailsSubmit} className={"auth-form-grid"} noValidate={true}>
                        <div>
                            <Label htmlFor={"displayName"} className={"auth-field-label"}>
                                Display Name
                                <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                            </Label>
                            <Input
                                ref={displayNameInputRef}
                                id={"displayName"}
                                type={"text"}
                                name={"displayName"}
                                autoComplete={"username"}
                                value={preferredUsername}
                                onChange={(event) => {
                                    setPreferredUsername(event.target.value);
                                    setErrorMessage(null);
                                }}
                                placeholder={"World2"}
                                required={true}
                                aria-required={"true"}
                                aria-invalid={errorMessage && !preferredUsername ? "true" : "false"}
                                aria-describedby={errorMessage ? "register-error" : undefined}
                            />
                        </div>

                        <div>
                            <p className={"auth-mailing-copy"}>
                                Stay connected with everything Animotive!
                            </p>
                            <fieldset className={"auth-radio-set"}>
                                <legend className={"auth-radio-legend"}>Mailing List</legend>
                                <div className={"auth-radio-row"} role={"radiogroup"} aria-label={"Mailing list preference"}>
                                    <label className={"auth-radio-item"} htmlFor={"mailing-list-yes"}>
                                        <input
                                            id={"mailing-list-yes"}
                                            type={"radio"}
                                            name={"mailingList"}
                                            value={"yes"}
                                            checked={mailingListOptIn === "yes"}
                                            onChange={(event) => setMailingListOptIn(event.target.value)}
                                            aria-label={"Yes, I want to join the mailing list"}
                                            className={"auth-mailing-radio"}
                                        />
                                        <span>Yes</span>
                                    </label>
                                    <label className={"auth-radio-item"} htmlFor={"mailing-list-no"}>
                                        <input
                                            id={"mailing-list-no"}
                                            type={"radio"}
                                            name={"mailingList"}
                                            value={"no"}
                                            checked={mailingListOptIn === "no"}
                                            onChange={(event) => setMailingListOptIn(event.target.value)}
                                            aria-label={"No, I don't want to join the mailing list"}
                                            className={"auth-mailing-radio"}
                                        />
                                        <span>No</span>
                                    </label>
                                </div>
                            </fieldset>
                            <p className={"auth-mailing-note"}>
                                {mailingListOptIn === "yes"
                                    ? "Yes! I'd like to occasionally receive info, product updates and offers."
                                    : "No, I don't want to receive info, product updates and offers."}
                            </p>
                        </div>

                        <div>
                            <Label htmlFor={"email"} className={"auth-field-label"}>
                                Email
                                <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                            </Label>
                            <Input
                                ref={emailInputRef}
                                id={"email"}
                                type={"email"}
                                name={"email"}
                                autoComplete={"email"}
                                value={email}
                                onChange={(event) => {
                                    setEmail(event.target.value);
                                    setErrorMessage(null);
                                }}
                                placeholder={"thomas.passeli@retinize.com"}
                                required={true}
                                aria-required={"true"}
                                aria-invalid={errorMessage && !email ? "true" : "false"}
                                aria-describedby={errorMessage ? "register-error" : undefined}
                            />
                        </div>

                        <div>
                            <Label htmlFor={"password"} className={"auth-field-label"}>
                                Password
                                <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                            </Label>
                            <div className={"auth-password-wrap"}>
                                <Input
                                    ref={passwordInputRef}
                                    id={"password"}
                                    type={showPassword ? "text" : "password"}
                                    name={"password"}
                                    autoComplete={"new-password"}
                                    onChange={() => setErrorMessage(null)}
                                    placeholder={"••••••••"}
                                    required={true}
                                    aria-required={"true"}
                                    aria-invalid={errorMessage && !(passwordInputRef.current?.value ?? "") ? "true" : "false"}
                                    aria-describedby={errorMessage ? "register-error" : undefined}
                                    className={"auth-input-with-icon"}
                                />
                                <button
                                    type={"button"}
                                    onClick={() => setShowPassword((current) => !current)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-pressed={showPassword ? "true" : "false"}
                                    aria-controls={"password"}
                                    className={"auth-password-toggle"}
                                >
                                    {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor={"confirmPassword"} className={"auth-field-label"}>
                                Confirm Password
                                <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                            </Label>
                            <div className={"auth-password-wrap"}>
                                <Input
                                    ref={confirmPasswordInputRef}
                                    id={"confirmPassword"}
                                    type={showConfirmPassword ? "text" : "password"}
                                    name={"confirmPassword"}
                                    autoComplete={"new-password"}
                                    onChange={() => setErrorMessage(null)}
                                    placeholder={"••••••••"}
                                    required={true}
                                    aria-required={"true"}
                                    aria-invalid={errorMessage && (passwordInputRef.current?.value ?? "") !== (confirmPasswordInputRef.current?.value ?? "") ? "true" : "false"}
                                    aria-describedby={errorMessage ? "register-error" : undefined}
                                    className={"auth-input-with-icon"}
                                />
                                <button
                                    type={"button"}
                                    onClick={() => setShowConfirmPassword((current) => !current)}
                                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                    aria-pressed={showConfirmPassword ? "true" : "false"}
                                    aria-controls={"confirmPassword"}
                                    className={"auth-password-toggle"}
                                >
                                    {showConfirmPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                                </button>
                            </div>
                        </div>

                        <div className={"auth-checks"}>
                            <label className={"auth-check-label"} htmlFor={"terms-checkbox"}>
                                <Checkbox
                                    id={"terms-checkbox"}
                                    checked={acceptedTerms}
                                    onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                                    aria-required={"true"}
                                    aria-invalid={errorMessage && !acceptedTerms ? "true" : "false"}
                                    aria-describedby={errorMessage ? "register-error" : undefined}
                                    className={"auth-check-box"}
                                    required={true}
                                />
                                <span>
                                    I agree to Animotive&apos;s{" "}
                                    <button
                                        type={"button"}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setShowTermsModal(true);
                                        }}
                                        aria-label={"View Terms & Conditions (opens dialog)"}
                                        className={"auth-inline-action"}
                                    >
                                        Terms & Conditions
                                    </button>
                                    <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                                </span>
                            </label>

                            <label className={"auth-check-label"} htmlFor={"privacy-checkbox"}>
                                <Checkbox
                                    id={"privacy-checkbox"}
                                    checked={acceptedPrivacyPolicy}
                                    onCheckedChange={(checked) => setAcceptedPrivacyPolicy(checked === true)}
                                    aria-required={"true"}
                                    aria-invalid={errorMessage && !acceptedPrivacyPolicy ? "true" : "false"}
                                    aria-describedby={errorMessage ? "register-error" : undefined}
                                    className={"auth-check-box"}
                                    required={true}
                                />
                                <span>
                                    I agree to Animotive&apos;s{" "}
                                    <button
                                        type={"button"}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setShowPrivacyModal(true);
                                        }}
                                        aria-label={"View Privacy Policy (opens dialog)"}
                                        className={"auth-inline-action"}
                                    >
                                        Privacy Policy
                                    </button>
                                    <span className={"auth-required-mark"} aria-label={"required"}>*</span>
                                </span>
                            </label>
                        </div>

                        <Button
                            type={"submit"}
                            disabled={isSubmitting}
                            aria-busy={isSubmitting ? "true" : "false"}
                            fullWidth={true}
                        >
                            {isSubmitting ? <Loader2 size={18}/> : null}
                            {isSubmitting ? "Creating account..." : "Create Account"}
                        </Button>
                    </form>
                )}

                <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
                <PrivacyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />

                {/* Google sign-up disabled — see docs/deployment/google-integration-shelved.md
                {!isAwaitingConfirmation && (
                    <>
                        <div className={"auth-google-divider"}>or</div>
                        <button
                            type={"button"}
                            className={"auth-google-btn"}
                            onClick={() => {
                                void handleGoogleSignUp();
                            }}
                            aria-label={"Sign up with Google"}
                        >
                            <svg className={"auth-google-icon"} width={"18"} height={"18"} viewBox={"0 0 24 24"} aria-hidden={"true"}>
                                <path fill={"#4285F4"} d={"M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"}/>
                                <path fill={"#34A853"} d={"M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"}/>
                                <path fill={"#FBBC05"} d={"M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"}/>
                                <path fill={"#EA4335"} d={"M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"}/>
                            </svg>
                            Sign up with Google
                        </button>
                    </>
                )}
                */}
            </div>
        </div>
    );
}
